import { AppDestroy, provideAppDestroy } from '@bcf-vanilla-ts-v1-shared/di/app-destroy';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { BcfLitRoutes, RouteConfig } from '@tmf-shared-platform/router/internal';
import { isServer } from 'lit';
import { equals } from 'rambdax-v11';
import {
  auditTime,
  BehaviorSubject,
  distinctUntilChanged,
  fromEvent,
  Observable,
  ReplaySubject,
  Subject,
  takeUntil
} from 'rxjs';

export type Params = {
  [key: string]: string | undefined;
};

export type QueryParams = {
  [key: string]: string | number | boolean | undefined | string[];
};

export class ActivatedRoute {
  private _routes: BcfLitRoutes[] = [];
  private _currentRoute$: ReplaySubject<RouteConfig> = new ReplaySubject<RouteConfig>(1);
  private _currentParams: Params | undefined;
  private _currentFragment: string | undefined;
  private _currentRaqQueryParamsString: string | undefined;

  private _data$: ReplaySubject<Params> = new ReplaySubject<Params>(1);
  private _params$: BehaviorSubject<Params> = new BehaviorSubject<Params>({});
  private _fragment$: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
  private _queryParams$: BehaviorSubject<QueryParams> = new BehaviorSubject<QueryParams>({});
  private _popState$: Subject<void> = new Subject<void>();

  private _doCheckAtNextTick$: Subject<void> = new Subject<void>();

  constructor(private _appDestroy: AppDestroy) {
    if (!isServer) {
      const observeEvent = (event: string, subFn: () => void): void => {
        fromEvent(window, event).pipe(takeUntil(this._appDestroy.destroy$)).subscribe(subFn);
      };
      observeEvent('click', () => this._doCheckAtNextTick$.next(undefined));
      observeEvent('popstate', () => {
        this._doCheckAtNextTick$.next(undefined);
        this._popState$.next(undefined);
      });
      observeEvent('hashchange', () => this._doCheckAtNextTick$.next(undefined));
      observeEvent('routerUpdated', () => this._doCheckAtNextTick$.next(undefined));
      observeEvent('routerReplaced', () => this._doCheckAtNextTick$.next(undefined));
    }

    this._checkAtNextTick();
  }

  public addRouter(routes: BcfLitRoutes): void {
    this._routes = [...this._routes, routes];
  }

  public removeRouter(routes: BcfLitRoutes): void {
    this._routes = this._routes.filter((innerRoutes: BcfLitRoutes) => innerRoutes !== routes);
  }

  public get params$(): Observable<Params> {
    return this._params$.asObservable();
  }

  public get queryParams$(): Observable<QueryParams> {
    return this._queryParams$.pipe(auditTime(20)); // this audit time must be grater the checkAtNextTick
  }

  public get popState$(): Observable<void> {
    return this._popState$;
  }

  public getQueryParamsTyped<T>(): Observable<T> {
    return this.queryParams$ as Observable<T>;
  }

  public get fragment$(): Observable<string | undefined> {
    return this._fragment$.asObservable();
  }

  public get data$(): Observable<Params> {
    return this._data$.pipe(distinctUntilChanged((previous: Params, current: Params) => equals(previous, current)));
  }

  private get _lastRouter(): BcfLitRoutes | undefined {
    return this._routes[this._routes.length - 1];
  }

  public get currentRoute$(): Observable<RouteConfig> {
    return this._currentRoute$;
  }

  private _checkAtNextTick(): void {
    this._doCheckAtNextTick$.pipe(auditTime(10), takeUntil(this._appDestroy.destroy$)).subscribe(() => {
      if (this._lastRouter?._currentRoute) {
        this._currentRoute$.next(this._lastRouter?._currentRoute);
      }
      this._checkParams();
      this._checkFragment();
      this._checkQueryParams();
      this._checkData();
    });
  }

  private _checkParams(): void {
    if (this._lastRouter && this._currentParams !== this._lastRouter.params) {
      this._params$.next(this._lastRouter!.params);
      this._currentParams = this._lastRouter.params;
    }
  }

  private _checkData(): void {
    type RouteParams = Record<string, string | undefined>;

    function extractParams(currentParams: RouteParams): RouteParams {
      const container: RouteParams = {};
      for (const [key, value] of Object.entries(currentParams)) {
        if (key === '0') {
          continue;
        }
        if (value?.includes('/')) {
          continue;
        }
        container[key] = value;
      }
      return container;
    }

    let dataContainer: Record<string, any> = {};
    for (const route of this._routes) {
      if (route._currentRoute?.data) {
        dataContainer = {
          ...dataContainer,
          ...route._currentRoute.data
        };
      }
      if (route._currentRoute?.dataGenerator) {
        dataContainer = {
          ...dataContainer,
          ...route._currentRoute.dataGenerator(extractParams(route._currentParams))
        };
      }
    }
    this._data$.next(dataContainer);
  }

  private _checkFragment(): void {
    const currentFragment: string | undefined =
      window.location.hash.length > 0 ? window.location.hash.slice(1) : undefined;
    if (this._currentFragment !== currentFragment) {
      this._fragment$.next(currentFragment);
      this._currentFragment = currentFragment;
    }
  }

  private _checkQueryParams(): void {
    if (this._currentRaqQueryParamsString !== window.location.search) {
      const urlSearchParams: URLSearchParams = new URLSearchParams(window.location.search);
      const queryParams: QueryParams = {};
      for (const [key, value] of urlSearchParams.entries()) {
        queryParams[key] = value;
        if (value === 'true') {
          queryParams[key] = true;
        }
        if (queryParams[key] === 'false') {
          queryParams[key] = false;
        }
        if (!isNaN(Number(value))) {
          queryParams[key] = Number(value);
        }
      }
      this._queryParams$.next(queryParams);
      this._currentRaqQueryParamsString = window.location.search;
    }
  }
}

export function provideActivatedRoute(): ActivatedRoute {
  return provideSingleton(ActivatedRoute, () => new ActivatedRoute(provideAppDestroy()));
}
